import api from './api';

/**
 * Client-side cache of the product-wide memo rating, read synchronously by the
 * structured-data builder (buildStructuredData) so the homepage can emit an
 * aggregateRating. Kept here (not fetched inside the sync builder) because the
 * fetch is async but the JSON-LD is written synchronously on every route change.
 *
 * The whole point of this feature: the aggregateRating is emitted ONLY from these
 * real, user-submitted ratings, and only once there's a credible sample — never
 * fabricated. See src/structuredData.ts and the memo_ratings table.
 */

export interface ProductRating {
  average: number;
  count: number;
}

/**
 * Minimum genuine ratings before we surface an aggregateRating in search. A lone
 * self-rating is thin and reads as spam; a real sample is credible (and Google
 * discounts tiny counts anyway). Until the count clears this bar the homepage
 * emits no aggregateRating at all — exactly today's state.
 */
export const RATING_MIN_SAMPLE = 5;

let cached: ProductRating | null = null;
let inFlight: Promise<void> | null = null;

/** The last loaded aggregate, or null if not yet loaded / failed to load. */
export function getCachedProductRating(): ProductRating | null {
  return cached;
}

/** True when there's a large enough genuine sample to publish an aggregateRating. */
export function hasPublishableRating(r: ProductRating | null): r is ProductRating {
  return !!r && r.count >= RATING_MIN_SAMPLE && r.average > 0;
}

/**
 * Fetch the aggregate once and cache it. Safe to call repeatedly (dedupes the
 * in-flight request). Never throws — on failure the cache stays null and the
 * structured data simply omits aggregateRating.
 */
export function loadProductRating(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = api
    .getProductRating({})
    .then((r) => {
      cached = { average: r.average, count: r.count };
    })
    .catch(() => {
      /* non-fatal — leave cache null, no aggregateRating emitted */
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}
